import { useEffect, useMemo, useRef, useState, Suspense, lazy } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import { Core, http } from "@/api";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  useToast,
} from "@homy/ui";
import { themes, getTheme, setTheme } from "@/lib/theme";
import { LoadingFallback } from "@/components/layout/LoadingFallback";
import { queryKeys } from "@/lib/queryKeys";
import { useInboxWebSocket } from "@/features/inbox/hooks/useInboxWebSocket";
import ModalErrorBoundary from "@/components/ModalErrorBoundary";

import {
  buildAppNavGroups,
  computeActiveGroupIds,
} from "@/components/layout/appNavConfig";
import MobileBottomNav from "./MobileBottomNav";
import CommandPalette from "@/features/core/components/CommandPalette";
import { AppSidebar } from "./AppSidebar";
import { AppMobileDrawer } from "./AppMobileDrawer";
import { AppHeader } from "./AppHeader";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { SkipLink } from "./SkipLink";
import { useView } from "@/context/ViewContext";

const VendorOnboardingWizard = lazy(() =>
  import("@/features/onboarding").then((m) => ({ default: m.VendorOnboardingWizard }))
);
const ResidentOnboardingWizard = lazy(() =>
  import("@/features/onboarding").then((m) => ({ default: m.ResidentOnboardingWizard }))
);
const OrgOnboardingWizard = lazy(() =>
  import("@/features/onboarding").then((m) => ({ default: m.OrgOnboardingWizard }))
);
const GetStartedWizard = lazy(() =>
  import("@/features/onboarding").then((m) => ({ default: m.GetStartedWizard }))
);
import ErrorBoundary from "@/components/ErrorBoundary";
import { AlertCircle } from "lucide-react";

const SIDEBAR_STORAGE_KEY = "homy_sidebar_collapsed";
const NAV_GROUPS_OPEN_STORAGE_KEY = "homy_nav_open_group_ids";

function loadStoredNavGroupIds() {
  try {
    const raw = localStorage.getItem(NAV_GROUPS_OPEN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

const GlobalSearchModal = CommandPalette;

export default function AppLayout() {
  const { t } = useTranslation(["common", "agents"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, endImpersonation } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const val = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return val === "true";
    } catch (e) {
      return false;
    }
  });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openNavGroups, setOpenNavGroups] = useState(() => {
    const base = new Set(computeActiveGroupIds(location.pathname, user));
    loadStoredNavGroupIds().forEach((id) => base.add(id));
    return base;
  });

  const [getStartedDismissed, setGetStartedDismissed] = useState(() => {
    try {
      return localStorage.getItem("homy_onboarding_skipped") === "true";
    } catch {
      return false;
    }
  });

  const showGetStartedWizard = Boolean(
    user && !user.organizationId && !getStartedDismissed
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const { currentView, activeWizard, closeWizard, onCompleteWizard } = useView();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: unreadCountData } = useQuery({
    queryKey: queryKeys.inbox.unreadCount(),
    queryFn: async () => {
      const { data } = await http.get("/api/inbox/unread-count");
      return data;
    },
    enabled: !!user,
  });
  const unreadInboxCount = unreadCountData?.count || 0;

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const tRef = useRef(t);
  tRef.current = t;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useInboxWebSocket({
    user,
    onNotification: (item) => {
      toastRef.current({
        title: item.title || tRef.current("common:notifications", "Notification"),
        description: item.description || "",
        variant: "default",
      });
      queryClientRef.current.invalidateQueries({ queryKey: queryKeys.inbox.all });
      queryClientRef.current.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const navGroups = useMemo(
    () => buildAppNavGroups(t, user, currentView, unreadInboxCount),
    [t, user, currentView, unreadInboxCount]
  );

  useEffect(() => {
    setOpenNavGroups((prev) => {
      const next = new Set(prev);
      computeActiveGroupIds(location.pathname, user, currentView).forEach((id) =>
        next.add(id),
      );
      return next;
    });
  }, [location.pathname, user, currentView]);

  useEffect(() => {
    try {
      localStorage.setItem(
        NAV_GROUPS_OPEN_STORAGE_KEY,
        JSON.stringify([...openNavGroups]),
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [openNavGroups]);

  const toggleNavGroup = (id) => {
    setOpenNavGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { data: organization } = useQuery({
    queryKey: queryKeys.organizations.current(),
    queryFn: () => Core.api.getMyOrganization(),
  });

  useEffect(() => {
    // 1281px catches 720p screens and the standard 1280x720 Playwright QA viewport
    const checkScreenSize = () => window.innerWidth < 1281;
    let wasSmall = checkScreenSize();

    if (wasSmall) {
      setSidebarCollapsed(true);
    } else {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
      setSidebarCollapsed(stored);
    }

    const handleResize = () => {
      const isSmall = checkScreenSize();
      if (isSmall && !wasSmall) {
        setSidebarCollapsed(true);
      }
      wasSmall = isSmall;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const theme = getTheme();
    if (theme === themes.system) {
      setIsDarkMode(window.matchMedia("(prefers-color-scheme: dark)").matches);
    } else {
      setIsDarkMode(theme === themes.dark);
    }
  }, []);

  const toggleTheme = () => {
    const next = isDarkMode ? themes.light : themes.dark;
    setTheme(next);
    setIsDarkMode(next === themes.dark);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen w-screen bg-background bg-atmospheric">
      <SkipLink />
      <ModalErrorBoundary onClose={() => setSearchOpen(false)}>
        <GlobalSearchModal open={searchOpen} onOpenChange={setSearchOpen} />
      </ModalErrorBoundary>
      <AppSidebar
        collapsed={sidebarCollapsed}
        onItemClick={handleNavClick}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
        navGroups={navGroups}
        openGroups={openNavGroups}
        onToggleGroup={toggleNavGroup}
      />

      <AppMobileDrawer
        open={mobileMenuOpen}
        onOpenChange={(open) => !open && setMobileMenuOpen(false)}
        navGroups={navGroups}
        openGroups={openNavGroups}
        onToggleGroup={toggleNavGroup}
        onNavigate={handleNavClick}
      />

      <div className="flex-1 flex flex-col min-w-0 h-full">
        {user?.isImpersonated && (
          <ImpersonationBanner
            user={user}
            organization={organization}
            onExit={endImpersonation}
          />
        )}
        <AppHeader
          onOpenMobileMenu={() => setMobileMenuOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          organization={organization}
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          user={user}
          onLogout={handleLogout}
          onNavigate={navigate}
          onExitImpersonation={endImpersonation}
        />

        <main
          id="main-content"
          tabIndex={-1}
          className="relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-transparent pb-[calc(4rem+env(safe-area-inset-bottom,0px))] md:pb-0"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-header-fade opacity-95"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-grid-subtle [mask-image:linear-gradient(to_bottom,black_5%,transparent_90%)] opacity-55"
          />
          <div className="relative z-10 flex min-h-0 w-full min-w-0 flex-1 flex-col bg-transparent px-3.5 py-2 sm:px-5 sm:py-3 lg:px-6 lg:py-3.5">
            <ErrorBoundary
              key={location.pathname}
              fallback={({ error, resetErrorBoundary }) => (
                <div className="flex flex-1 items-center justify-center p-4" data-testid="outlet-error-boundary">
                  <Card className="w-full max-w-lg p-6 border-border shadow-md">
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle className="text-foreground">
                        {t("common:errorBoundary.title", "Something went wrong")}
                      </AlertTitle>
                      <AlertDescription className="text-muted-foreground mt-1">
                        {t("common:errorBoundary.message", "An unexpected error occurred while loading this section.")}
                      </AlertDescription>
                    </Alert>
                    {error && (
                      <div className="mb-4 p-3 rounded-lg bg-muted text-muted-foreground text-xs font-mono overflow-auto max-h-32">
                        <pre className="whitespace-pre-wrap break-words">
                          {error.message || String(error)}
                        </pre>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetErrorBoundary}
                        className="min-h-[44px] sm:min-h-0"
                      >
                        {t("common:actions.retry", "Try again")}
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            >
              <Suspense fallback={<LoadingFallback />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
        <MobileBottomNav unreadInboxCount={unreadInboxCount} />
        <Suspense fallback={null}>
          <ModalErrorBoundary onClose={closeWizard}>
            <ResidentOnboardingWizard
              open={activeWizard === "become_resident"}
              onOpenChange={(open) => !open && closeWizard()}
              onComplete={onCompleteWizard}
            />
          </ModalErrorBoundary>
          <ModalErrorBoundary onClose={closeWizard}>
            <VendorOnboardingWizard
              open={activeWizard === "become_vendor"}
              onOpenChange={(open) => !open && closeWizard()}
              onComplete={onCompleteWizard}
            />
          </ModalErrorBoundary>
          <ModalErrorBoundary onClose={closeWizard}>
            <OrgOnboardingWizard
              open={activeWizard === "create_or_join_org"}
              onOpenChange={(open) => !open && closeWizard()}
              onComplete={onCompleteWizard}
            />
          </ModalErrorBoundary>
          <ModalErrorBoundary onClose={() => setGetStartedDismissed(true)}>
            <GetStartedWizard
              open={showGetStartedWizard}
              onOpenChange={(open) => {
                if (!open) {
                  setGetStartedDismissed(true);
                }
              }}
            />
          </ModalErrorBoundary>
        </Suspense>
      </div>
    </div>
  );
}
